import React, { useEffect, useState } from "react";

type MorphLinkProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "children"
> & {
  children: string;
};

const characters = "0123456789abcdefghijklmnopqrstuvwxyz";
const morphInterval = 136;

const scramble = (text: string) =>
  Array.from(text)
    .map((character) => {
      if (/\s/.test(character)) return character;
      return characters[Math.floor(Math.random() * characters.length)];
    })
    .join("");

const MorphLink: React.FC<MorphLinkProps> = ({ children, ...linkProps }) => {
  const [displayText, setDisplayText] = useState(children);
  const [isReadable, setIsReadable] = useState(false);
  const visibleText = isReadable ? children : displayText;

  useEffect(() => {
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const supportsHover = window.matchMedia("(hover: hover)").matches;

    if (reducedMotion || !supportsHover || isReadable) {
      setDisplayText(children);
      return;
    }

    let animationFrame = 0;
    let lastMorphTime = 0;

    const animate = (time: number) => {
      if (!lastMorphTime || time - lastMorphTime >= morphInterval) {
        setDisplayText(scramble(children));
        lastMorphTime = time;
      }

      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [children, isReadable]);

  return (
    <a
      {...linkProps}
      aria-label={children}
      style={{ ...linkProps.style, width: `${children.length}ch` }}
      onBlur={() => setIsReadable(false)}
      onFocus={() => setIsReadable(true)}
      onMouseEnter={() => setIsReadable(true)}
      onMouseLeave={() => setIsReadable(false)}
      onPointerEnter={() => setIsReadable(true)}
      onPointerLeave={() => setIsReadable(false)}
    >
      <span aria-hidden="true">{visibleText}</span>
    </a>
  );
};

export default MorphLink;
